import UsersManager from "../components/UsersManager";
import { trpc } from "../../main/components/trpc.ts";
import useUser from "../hooks/useUser.ts";
import React, { useEffect } from "react";
import { useLocation } from "wouter";
import CreateUser from "../components/CreateUser.tsx";
import styles from "./UsersManagerContainer.module.css";
import OrgManagerContainer from "./OrgManagerContainer.tsx";
import RespondUsersPanel from "../components/RespondUsersPanel.tsx";
import MutuellesAdminPanel from "../components/MutuellesAdminPanel.tsx";
import { inferRouterOutputs } from "@trpc/server/unstable-core-do-not-import";
import type { AppRouter } from "cadendar-trpc-server";

export type SmsCountMap = { [userId: string]: number };

type UserRouterOutput = inferRouterOutputs<AppRouter>["user"];
type XRayStorageUsageRow = UserRouterOutput["getXRayStorageUsageForAdmin"][number];
type XRayStorageBackfillSummary =
  UserRouterOutput["backfillXRayStorageSizesForAdmin"];

const numberFormatter = new Intl.NumberFormat("fr-FR");

function formatBytes(bytes: number | null) {
  if (bytes === null) {
    return "-";
  }

  if (bytes === 0) {
    return "0 B";
  }

  const sign = bytes < 0 ? "-" : "";
  const absoluteBytes = Math.abs(bytes);

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(
    Math.floor(Math.log(absoluteBytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = absoluteBytes / Math.pow(1024, index);

  return `${sign}${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
}

const UsersManagerContainer = () => {
  const user = useUser();
  const allOrgs = trpc.user.getAllOrgs.useQuery().data;
  const [, navigate] = useLocation();
  const [isCreatingUser, setIsCreatingUser] = React.useState(false);
  const [xRayStorageS3Rows, setXRayStorageS3Rows] = React.useState<
    XRayStorageUsageRow[] | null
  >(null);
  const [xRayStorageBackfillSummary, setXRayStorageBackfillSummary] =
    React.useState<XRayStorageBackfillSummary | null>(null);
  const [xRayStorageS3Error, setXRayStorageS3Error] = React.useState("");
  const [xRayStorageBackfillError, setXRayStorageBackfillError] =
    React.useState("");
  useEffect(() => {
    if (!user || user.username !== "admin") {
      navigate("/");
    }
  }, [user]);
  const { data: smsSentCountMap } =
    trpc.reminder.getEverySmsSentCount.useQuery();
  const { data: fetchedUsers } = trpc.user.getAllUsersForAdmin.useQuery();
  const { data: comptaUsers } = trpc.user.getComptaUsersForAdmin.useQuery(undefined, {
    enabled: user?.username === "admin",
  });
  const { data: knownRespondUsers } = trpc.respond.getKnownUsersForAdmin.useQuery();
  const {
    data: xRayStorageDbRows,
    isLoading: isLoadingXRayStorageDbRows,
    error: xRayStorageDbError,
  } = trpc.user.getXRayStorageUsageForAdmin.useQuery(undefined, {
    enabled: user?.username === "admin",
  });
  const doctorUsers =
    (fetchedUsers && fetchedUsers.filter((u) => u.tag === "doctor")) || [];
  const secretaryUsers =
    (fetchedUsers && fetchedUsers.filter((u) => u.tag === "secretary")) || [];
  const users = [...doctorUsers, ...secretaryUsers];
  const trpcUtils = trpc.useContext();
  const updateDoctorMutation = trpc.user.updateDoctorByAdmin.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const updateSecretaryMutation = trpc.user.updateSecretaryByAdmin.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const updateUserActiveStatusMutation =
    trpc.user.updateActiveStatus.useMutation({
      onSettled: () => {
        trpcUtils.user.invalidate();
      },
    });
  const assignOrgToUserMutation = trpc.user.assignOrgToUser.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const assignSecretaryToDoctorMutation =
    trpc.user.assignSecretaryToDoctor.useMutation({
      onSettled: () => {
        trpcUtils.user.invalidate();
      },
    });
  const linkComptaUserToDoctorMutation = trpc.user.linkComptaUserToDoctor.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const unlinkComptaUserFromDoctorMutation =
    trpc.user.unlinkComptaUserFromDoctor.useMutation({
      onSettled: () => {
        trpcUtils.user.invalidate();
      },
    });
  const linkRespondUserMutation =
    trpc.respond.linkRespondUserToCadendarUser.useMutation({
      onSettled: () => {
        trpcUtils.respond.invalidate();
      },
    });
  const createCadendarUserFromRespondUserMutation =
    trpc.respond.createCadendarUserFromRespondUser.useMutation({
      onSettled: () => {
        trpcUtils.respond.invalidate();
        trpcUtils.user.invalidate();
      },
    });
  const unlinkRespondUserMutation = trpc.respond.unlinkRespondUser.useMutation({
    onSettled: () => {
      trpcUtils.respond.invalidate();
    },
  });
  const xRayStorageS3Mutation = trpc.user.getXRayStorageUsageS3ForAdmin.useMutation({
    onSuccess: (rows) => {
      setXRayStorageS3Rows(rows);
      setXRayStorageS3Error("");
    },
    onError: (error) => {
      setXRayStorageS3Error(
        error.message || "Impossible de calculer l'usage S3 des radios.",
      );
    },
  });
  const xRayStorageBackfillMutation =
    trpc.user.backfillXRayStorageSizesForAdmin.useMutation({
      onSuccess: (summary) => {
        setXRayStorageBackfillSummary(summary);
        setXRayStorageBackfillError("");
        setXRayStorageS3Rows(null);
        trpcUtils.user.getXRayStorageUsageForAdmin.invalidate();
      },
      onError: (error) => {
        setXRayStorageBackfillError(
          error.message || "Impossible de backfiller les tailles DB des radios.",
        );
      },
    });

  const onAssignOrg = (userId: string, orgId: number) => {
    assignOrgToUserMutation.mutate({ userId, orgId });
  };
  // const onAddUser = (
  //   receptorId: string,
  //   index: number,
  //   userId: string | null
  // ) => {
  //   const user = users && users.find((u) => u.id === receptorId);
  //   if (!user) {
  //     throw new Error("no user found in onAddUser");
  //   }
  //   let userIds: string[] = [];
  //   if (user.tag === "doctor") {
  //     userIds = user.secretaries || [];
  //   }
  //   if (user.tag === "secretary") {
  //     throw new Error("secretary cannot have a secretary");
  //   }
  //   if (userId === null) {
  //     if (userIds[index]) {
  //       userIds.splice(index, 1);
  //     }
  //   }
  //   if (userId && userIds.includes(userId)) {
  //     return;
  //   }
  //   if (userId !== null) {
  //     if (index !== 0) {
  //       userIds.splice(index, 1, userId);
  //     } else {
  //       //index = 0, on ajoute à la fin
  //       userIds.push(userId);
  //     }
  //   }

  //   const updatedUser =
  //     user.tag === "secretary"
  //       ? { ...user, doctor_id: userIds }
  //       : user.tag === "doctor"
  //       ? { ...user, secretaries: userIds }
  //       : null;
  //   updatedUser && updateUserMutation.mutate(updatedUser);
  // };
  const onUpdateFullName = (userId: string, fullname: string) => {
    if (doctorUsers.find((u) => u.id === userId)) {
      updateDoctorMutation.mutate({
        doctor_id: userId,
        key: "fullname",
        value: fullname,
      });
    } else if (secretaryUsers.find((u) => u.id === userId)) {
      updateSecretaryMutation.mutate({
        secretary_id: userId,
        key: "fullname",
        value: fullname,
      });
    }
  };
  const onUpdateMessageName = (userId: string, messagename: string) => {
    if (doctorUsers.find((u) => u.id === userId)) {
      updateDoctorMutation.mutate({
        doctor_id: userId,
        key: "messagename",
        value: messagename,
      });
    } else if (secretaryUsers.find((u) => u.id === userId)) {
      updateSecretaryMutation.mutate({
        secretary_id: userId,
        key: "messagename",
        value: messagename,
      });
    }
  };
  const onUpdateActiveStatus = (userId: string, active: boolean) => {
    const user = users && users.find((u) => u.id === userId);
    if (!user) {
      throw new Error("no user found in onUpdateActiveStatus");
    }
    updateUserActiveStatusMutation.mutate({ userId, active });
  };
  const makeSecretaireMutation = trpc.user.makeSecretaire.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const makeDoctorMutation = trpc.user.makeDoctor.useMutation({
    onSettled: () => {
      trpcUtils.user.invalidate();
    },
  });
  const onUpdateRole = (
    userId: string,
    role: "docteur" | "secrétaire" | null | undefined
  ) => {
    const user = users && users.find((u) => u.id === userId);
    if (!user) {
      throw new Error("no user found in onUpdateRole");
    }
    if (role === "secrétaire") {
      makeSecretaireMutation.mutate(userId);
    }
    if (role === "docteur") {
      makeDoctorMutation.mutate(userId);
    }
    if (role === null) {
      return;
    }
  };
  const onUpdateDocId = (userId: string, docId: string) => {
    const user = users && users.find((u) => u.id === userId);
    if (!user) {
      throw new Error("no user found in onUpdateDocId");
    }
    if (user.tag !== "doctor") {
      throw new Error("user is not a doctor in onUpdateDocId");
    }
    updateDoctorMutation.mutate({
      doctor_id: userId,
      key: "doc_id",
      value: docId,
    });
  };
  const onAssignSecretary = (doctorId: string, secretaryId: string) => {
    assignSecretaryToDoctorMutation.mutate({ doctorId, secretaryId });
  };
  const onLinkComptaUserToDoctor = (doctorId: string, comptaUserId: string) => {
    linkComptaUserToDoctorMutation.mutate({ doctorId, comptaUserId });
  };
  const onUnlinkComptaUserFromDoctor = (doctorId: string) => {
    unlinkComptaUserFromDoctorMutation.mutate({ doctorId });
  };
  const onUpdateGoogleId = (userId: string, googleId: string) => {
    const user = users && users.find((u) => u.id === userId);
    if (!user) {
      throw new Error("no user found in onUpdateGoogleId");
    }
    if (user.tag !== "doctor") {
      throw new Error("user is not a doctor in onUpdateGoogleId");
    }
    updateDoctorMutation.mutate({
      doctor_id: userId,
      key: "google_business_profile_id",
      value: googleId,
    });
  };
  const onLinkRespondUser = (respondUserId: string, cadendarUserId: string) => {
    linkRespondUserMutation.mutate({ respondUserId, cadendarUserId });
  };
  const onUnlinkRespondUser = (respondUserId: string) => {
    unlinkRespondUserMutation.mutate(respondUserId);
  };
  const onCreateAndLinkRespondUser = (options: {
    respondUserId: string;
    username: string;
    fullname: string;
    messagename: string;
    password: string;
  }) => {
    createCadendarUserFromRespondUserMutation.mutate(options);
  };

  const xRayStorageRows = xRayStorageS3Rows || xRayStorageDbRows || [];

  const onCalculateXRayStorageS3 = () => {
    setXRayStorageS3Error("");
    xRayStorageS3Mutation.mutate();
  };

  const onBackfillXRayStorageDb = () => {
    setXRayStorageBackfillError("");
    xRayStorageBackfillMutation.mutate();
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Stockage radios par owner</h2>
            <p className={styles.mutedText}>
              Chargement DB automatique. Le scan S3 est lancé à la demande pour comparer avec le bucket réel.
            </p>
          </div>
          <div className={styles.actionsRow}>
            <button
              type="button"
              onClick={onCalculateXRayStorageS3}
              disabled={xRayStorageS3Mutation.isPending}
            >
              {xRayStorageS3Mutation.isPending
                ? "Calcul S3 en cours..."
                : "Calculer l'usage S3"}
            </button>
            <button
              type="button"
              onClick={onBackfillXRayStorageDb}
              disabled={xRayStorageBackfillMutation.isPending}
            >
              {xRayStorageBackfillMutation.isPending
                ? "Backfill en cours..."
                : "Backfill des tailles DB"}
            </button>
          </div>
        </div>
        {isLoadingXRayStorageDbRows ? (
          <p className={styles.mutedText}>Chargement des tailles DB des radios...</p>
        ) : null}
        {xRayStorageDbError ? (
          <p className={styles.errorMessage}>
            {xRayStorageDbError.message || "Impossible de charger les tailles DB des radios."}
          </p>
        ) : null}
        {xRayStorageS3Error ? (
          <p className={styles.errorMessage}>{xRayStorageS3Error}</p>
        ) : null}
        {xRayStorageBackfillError ? (
          <p className={styles.errorMessage}>{xRayStorageBackfillError}</p>
        ) : null}
        {xRayStorageBackfillSummary ? (
          <p className={styles.successMessage}>
            Backfill terminé: {numberFormatter.format(xRayStorageBackfillSummary.updatedRows)} ligne(s) mise(s) à jour, {numberFormatter.format(xRayStorageBackfillSummary.scannedRows)} ligne(s) scannée(s), {numberFormatter.format(xRayStorageBackfillSummary.missingOriginalObjects)} original(aux) manquant(s), {numberFormatter.format(xRayStorageBackfillSummary.missingThumbnailObjects)} miniature(s) manquante(s).
          </p>
        ) : null}
        <div className={styles.tableWrapper}>
          <table className={styles.storageTable}>
            <thead>
              <tr>
                <th>Owner</th>
                <th>Org</th>
                <th>Radios DB</th>
                <th>DB originaux</th>
                <th>DB miniatures</th>
                <th>DB total</th>
                <th>S3 originaux</th>
                <th>S3 miniatures</th>
                <th>S3 total</th>
                <th>Delta</th>
                <th>Lignes DB sans taille</th>
              </tr>
            </thead>
            <tbody>
              {xRayStorageRows.map((row) => (
                <tr key={row.ownerId}>
                  <td>
                    <div>{row.fullname || row.username}</div>
                    <div className={styles.mutedText}>{row.username}</div>
                  </td>
                  <td>{row.organizationName || row.organizationId}</td>
                  <td className={styles.numericCell}>
                    {numberFormatter.format(row.radiosCount)}
                  </td>
                  <td className={styles.numericCell}>
                    {formatBytes(row.dbOriginalBytes)}
                  </td>
                  <td className={styles.numericCell}>
                    {formatBytes(row.dbThumbnailBytes)}
                  </td>
                  <td className={styles.numericCell}>{formatBytes(row.dbTotalBytes)}</td>
                  <td className={styles.numericCell}>
                    {row.s3OriginalBytes === null
                      ? "-"
                      : `${formatBytes(row.s3OriginalBytes)} (${numberFormatter.format(row.s3OriginalCount || 0)})`}
                  </td>
                  <td className={styles.numericCell}>
                    {row.s3ThumbnailBytes === null
                      ? "-"
                      : `${formatBytes(row.s3ThumbnailBytes)} (${numberFormatter.format(row.s3ThumbnailCount || 0)})`}
                  </td>
                  <td className={styles.numericCell}>
                    {formatBytes(row.s3TotalBytes)}
                  </td>
                  <td className={styles.numericCell}>
                    {row.deltaBytes === null ? "-" : formatBytes(row.deltaBytes)}
                  </td>
                  <td className={styles.numericCell}>
                    {numberFormatter.format(row.dbRowsWithoutKnownSize)}
                  </td>
                </tr>
              ))}
              {xRayStorageRows.length === 0 && !isLoadingXRayStorageDbRows ? (
                <tr>
                  <td colSpan={11} className={styles.emptyCell}>
                    Aucun owner trouvé pour le reporting radios.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <UsersManager
        users={users || []}
        comptaUsers={comptaUsers || []}
        smsCountMap={smsSentCountMap || {}}
        onAssignSecretaryToDoctor={onAssignSecretary}
        onLinkComptaUserToDoctor={onLinkComptaUserToDoctor}
        onUnlinkComptaUserFromDoctor={onUnlinkComptaUserFromDoctor}
        onUpdateFullName={onUpdateFullName}
        onUpdateMessageName={onUpdateMessageName}
        onAssignRole={onUpdateRole}
        onUpdateActiveStatus={onUpdateActiveStatus}
        onUpdateDocId={onUpdateDocId}
        organizations={allOrgs || []}
        onAssignOrg={onAssignOrg}
        onUpdateGoogleId={onUpdateGoogleId}
        isComptaLinkMutating={
          linkComptaUserToDoctorMutation.isPending ||
          unlinkComptaUserFromDoctorMutation.isPending
        }
      />
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setIsCreatingUser(true)}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          Créer un utilisateur
        </button>
      </div>
      {isCreatingUser && (
        <CreateUser
          onSuccess={() => setIsCreatingUser(false)}
          onCancel={() => setIsCreatingUser(false)}
        />
      )}
      <RespondUsersPanel
        knownRespondUsers={knownRespondUsers || []}
        cadendarUsers={users || []}
        onLink={onLinkRespondUser}
        onUnlink={onUnlinkRespondUser}
        onCreateAndLink={onCreateAndLinkRespondUser}
        isMutating={
          linkRespondUserMutation.isPending ||
          unlinkRespondUserMutation.isPending ||
          createCadendarUserFromRespondUserMutation.isPending
        }
      />
      <div className={styles.section}>
        <MutuellesAdminPanel
          networkName="Santéclair"
          networkLabel="Mutuelles Santéclair"
        />
      </div>
      <div className={styles.section}>
        <MutuellesAdminPanel
          networkName="Itelis"
          networkLabel="Mutuelles Itelis"
        />
      </div>
      <OrgManagerContainer />
    </div>
  );
};

export default UsersManagerContainer;
